/* eslint no-undef: 0 */
import React, { Component } from 'react';
import { Glyphicon } from 'react-bootstrap';
import moment from 'moment';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import { withTranslation, useTranslation } from 'react-i18next';

import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import classnames from 'classnames';
import JobStatus from 'components/modules/shipments/tracking/JobStatus';
import CargomaticMap from 'components/v2/map';
import CargomaticCard from 'components/v2/card';
import CargomaticInput from 'components/v2/input';
import SessionStore from 'stores/SessionStore';
import qs from 'qs';

import Spinner from 'components/ui/Spinner';

import TrackingHeader from 'components/ui/tracking-header';
import { sluggify } from 'utils/localization/utils';
import ShipmentHolds from '../shipments/ShipmentHolds';
import ShipmentDates from '../shipments/ShipmentDates';

const getNextStopMessage = (nextStop, t) => {
  try {
    const isLoggedIn = SessionStore.isLoggedIn();
    const address = isLoggedIn
      ? nextStop?.location[0].address
      : `${nextStop.location[0].city}, ${nextStop.location[0].state}`;

    if (nextStop.action === 'pickup') {
      const pickUpAddressSlug = nextStop.empty
        ? 'PICKING_UP_EMPTY_ADDRESS'
        : 'PICKING_UP_ADDRESS';
      return t(pickUpAddressSlug, {
        address,
      });
    }

    if (!nextStop.empty) {
      return t('DELIVERING_ADDRESS', {
        address,
      });
    }

    return t('RETURN_EMPTY_ADDRESS', {
      address,
    });
  } catch (e) {
    console.log(e);
  }
};

const ProTip = (props) => {
  const { message } = props;
  const { t } = useTranslation();
  return (
    <div className="pt-4">
      <div className="flex items-center w-full p-2 leading-none text-white bg-green lg:rounded-full lg:inline-flex">
        <span className="flex px-2 py-1 pr-1 text-sm font-bold uppercase rounded-full bg-green">
          {t('PRO_TIP')}
        </span>
        <Glyphicon className="pr-2 text-xsm" glyph="pencil" />
        <span>{message}</span>
      </div>
    </div>
  );
};

const Preview = (props) => {
  const {
    nextStop,
    shipment,
    identifier,
    isLoggedIn,
    disableScroll,
    showBookAppointment,
    history,
  } = props;
  const { t } = useTranslation();

  if (!shipment) return null;

  const jobStatusStyles = disableScroll
    ? {}
    : { maxHeight: 500, minHeight: 450, overflowY: 'scroll' };

  const handleBookAppointment = () => {
    let masterBol = '';
    let container = '';
    shipment.reference_numbers.forEach((reference) => {
      const { name, value } = reference;
      if (name === 'master_bol') {
        masterBol = value;
      }
      if (name === 'equipment_id') {
        container = value;
      }
    });

    history.push(
      `/appointments/new?masterbol=${masterBol}&container=${container}`,
    );
  };

  return (
    <CargomaticCard>
      <div className="flex justify-between pb-4 border-b border-solid border-grey-light">
        <div>
          <h2 className="p-0 m-0 text-grey-darker">{identifier}</h2>
        </div>
        <div className="pt-2">
          <span className="px-2 py-1 text-white truncate rounded bg-green">
            {titleize(t(sluggify(shipment.status)))}
          </span>
        </div>
      </div>
      {shipment && <ShipmentHolds shipment={shipment} />}
      {shipment && <ShipmentDates shipment={shipment} />}
      {showBookAppointment && (
        <button
          onClick={() => handleBookAppointment()}
          className="button button-green w-full flex justify-center items-center gap-x-2 my-2"
        >
          {' '}
          <Glyphicon glyph="list" />
          {t('BOOKING_APPOINTMENT')}
        </button>
      )}
      {nextStop && (
        <div className="pb-6 border-b border-solid border-grey-light">
          <h4 className="text-green">{t('NEXT_STOP')}</h4>
          <span>{getNextStopMessage(nextStop, t)}</span>
          {nextStop.window_start && nextStop.window_end && (
            <div>
              <span className="italic">
                {moment(nextStop.window_start).format('L, LT')}
              </span>{' '}
              -
              <span className="px-1 italic">
                {moment(nextStop.window_end).format('L, LT')}
              </span>
            </div>
          )}
        </div>
      )}
      <div style={jobStatusStyles}>
        <JobStatus
          job={shipment}
          hideReason={!isLoggedIn}
          hideAttachment={!isLoggedIn}
        />
      </div>
      <div className="border-t border-solid border-grey-light">
        <ProTip message={t('TRACKING_DATA_UPDATE_TIP')} />
      </div>
    </CargomaticCard>
  );
};

const SearchByReference = (props) => {
  const { t } = useTranslation();

  const { value, error, disabled, history } = props;
  return (
    <CargomaticCard>
      <h4 className="text-grey-darker">{t('REALTIME_TRACKING_INFO')}</h4>
      <div className="flex">
        <div className="w-5/6">
          <CargomaticInput
            hideLabel
            error={error}
            placeholder={t('SEARCH_BY_REF')}
            info={t('PROVIDE_KNOWN_TRACING')}
            onChange={(reference) => props.onChange(reference)}
            value={value}
          />
        </div>
        <div className="w-1/6 p-0 px-2">
          <button
            className="button button-green"
            onClick={() => props.onQuery()}
            disabled={disabled}
          >
            <div className="">
              {props.loading && <Spinner showMessage={false} />}
              {!props.loading && <span>{t('SEARCH')}</span>}
            </div>
          </button>
        </div>
      </div>
    </CargomaticCard>
  );
};

const ShipmentList = (props) => {
  const { shipments, identifier, selected } = props;
  const { t } = useTranslation();

  const getRowClassName = (index, id) => {
    return classnames({
      'bg-white': index % 2 !== 0,
      'bg-grey-lightest': index % 2 === 0,
      flex: 1,
      'py-2': 1,
      'px-2': 1,
      border: selected === id,
      'border-green': selected === id,
      'border-solid': selected === id,
      'cursor-pointer': 1,
    });
  };

  return (
    <CargomaticCard>
      <span>{t('MULTI_SHIPMENT_REF_MATCH_TIP')}</span>
      <div className="flex py-2 border-b border-solid border-grey-light">
        <div className="w-1/3">
          <span className="form-label">{t('REFERENCE')}</span>
        </div>
        <div className="w-1/3">
          <span className="form-label">{t('STATUS')}</span>
        </div>
        <div className="w-1/3">
          <span className="form-label">{t('CREATED')}</span>
        </div>
      </div>
      {shipments.map((shipment, i) => {
        return (
          <div
            className={getRowClassName(i, shipment._id)}
            key={i}
            onClick={() => props.onClick(shipment)}
          >
            <div className="w-1/3">
              <button
                className="font-bold text-blue-dark"
                onClick={() => props.onClick(shipment)}
              >
                {identifier}
              </button>
            </div>
            <div className="w-1/3">
              <span className="px-2 py-1 text-white break-words truncate rounded bg-green">
                {titleize(t(sluggify(shipment.status)))}
              </span>
            </div>
            <div className="w-1/3">
              <span className="">{moment(shipment.created_at).fromNow()}</span>
            </div>
          </div>
        );
      })}
    </CargomaticCard>
  );
};

const MAX_MAP_HEIGHT = '100vh';
const MIN_MAP_HEIGHT = 350;
const MD = 768;

class PublicShipmentTracking extends Component {
  constructor(props) {
    super(props);
    const { search } = props.location;
    const parsed = search ? qs.parse(search.replace('?', '')) : null;

    this.state = {
      error: null,
      shipments: [],
      selected: null,
      identifier: '',
      reference: search ? parsed.id : '',
      loading: false,
      // center: { lat: 90, lng: 118 },
      mapHeight: window.innerWidth >= MD ? MAX_MAP_HEIGHT : MIN_MAP_HEIGHT,
    };
  }

  _googleMapRef(m) {
    if (m) {
      this._map = m;
    }
  }

  componentDidMount() {
    const { reference } = this.state;
    if (reference !== '') this.query(); // If query param exists

    window.addEventListener('resize', () => {
      if (this._map) {
        if (window.innerWidth < MD && this.state.mapHeight !== MIN_MAP_HEIGHT) {
          this.setState({ mapHeight: MIN_MAP_HEIGHT }, () => {
            google.maps.event.trigger(
              this._map.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED,
              'resize',
            );
          });
        } else if (
          window.innerWidth > MD &&
          this.state.mapHeight !== MAX_MAP_HEIGHT
        ) {
          this.setState({ mapHeight: MAX_MAP_HEIGHT }, () => {
            google.maps.event.trigger(
              this._map.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED,
              'resize',
            );
          });
        }
      }
    });
  }

  setQueryParams(reference) {
    this.props.history.push({
      path: '/tracking',
      search: `id=${reference}`,
    });
  }

  query = async () => {
    const { reference } = this.state;
    const { t } = this.props;
    this.setState({ loading: true }, async () => {
      this.setQueryParams(reference);
      const shipments = await ShipmentWebAPIUtils.getTrackingShipments(
        reference,
      );
      const selected = shipments.length > 0 ? _.first(shipments) : null;
      const error = !selected ? new Error(t('NO_SHIPMENT_FOUND_FOR_REF')) : {};
      this.setState({
        loading: false,
        shipments,
        identifier: reference,
        selected,
        error,
      });
    });
  };

  nextStop(stops) {
    const sorted = _.sortBy(stops, 'sequence');
    return _.find(sorted, (stop) => {
      return !stop.complete;
    });
  }

  markers(stops) {
    const filtered = stops.filter((s) => {
      return !s.empty;
    });

    return filtered.map((s) => {
      return {
        coords: {
          lat: s.location[0].lat,
          lng: s.location[0].lng,
        },
        icon: {
          url: `/img/stop-${s.sequence}.png`,
        },
      };
    });
  }

  getDriverMarker(driver, current) {
    if (driver?.coords?.length > 0 && current) {
      return {
        coords: {
          lat: driver.coords[1],
          lng: driver.coords[0],
        },
        icon: {
          url: '/img/working-truck.png',
        },
      };
    }

    return null;
  }

  handleReferenceChange = (reference) => {
    const { t } = this.props;
    if (reference)
      return this.setState({ reference: reference.toUpperCase().trim() }); // Query uppercase optimized
    this.setState({
      reference: '',
      error: new Error(t('PROVIDE_SEARCH_VALUE')),
    });
  };

  isDisabled() {
    const { error, reference } = this.state;
    return !_.isEmpty(error) || !reference;
  }

  onShipmentSelect = (selected) => {
    this.setState({ selected });
  };

  render() {
    const isLoggedIn = SessionStore.isLoggedIn();
    const isConsignee = SessionStore.getUserType() === 'Consignee';
    const isCarrier = SessionStore.getUserType() === 'Carrier';

    const {
      mapHeight,
      selected,
      shipments,
      // loading,
      identifier,
      reference,
      error,
    } = this.state;
    let shipment;

    if (selected) {
      shipment = selected;
    } else {
      shipment = _.first(shipments);
    }

    const nextStop = shipment ? this.nextStop(shipment.stops) : {};
    const markers = shipment ? this.markers(shipment.stops) : [];
    const driverMarker = this.getDriverMarker(
      shipment?.driver,
      shipment?.current,
    );

    if (driverMarker) {
      markers.push(driverMarker);
    }

    const containerClassNames = classnames({
      absolute: !isLoggedIn,
      relative: isLoggedIn,
      'top-0': 1,
      'left-0': 1,
      'w-full': 1,
    });

    return (
      <div className={containerClassNames}>
        <TrackingHeader isLoggedIn={isLoggedIn} />
        <div className="flex">
          <div className="w-full">
            <div className="z-10 md:absolute md:right-0 md:mt-8 md:mr-8 md:w-1/3 sm:w-full sm:relative">
              <SearchByReference
                disabled={this.isDisabled()}
                error={error}
                onQuery={this.query}
                value={reference}
                loading={this.state.loading}
                onChange={this.handleReferenceChange}
                history={this.props.history}
              />
              {shipments.length > 1 && (
                <div className="py-2">
                  <ShipmentList
                    selected={shipment._id}
                    identifier={identifier}
                    shipments={shipments}
                    onClick={this.onShipmentSelect}
                  />
                </div>
              )}
            </div>

            <div className="w-full md:absolute md:left-0">
              <div
                className="h-full sm:h-200px"
                style={{ height: this.state.mapHeight }}
              >
                <CargomaticMap
                  getMapComponent={(m) => this._googleMapRef(m)}
                  defaultZoom={10}
                  height={mapHeight}
                  markers={markers}
                />
              </div>
            </div>
            <div className="md:absolute md:left-0 md:ml-8 md:mt-8 md:w-1/3 sm:w-full sm:relative">
              {shipment && (
                <Preview
                  disableScroll={mapHeight === MIN_MAP_HEIGHT}
                  shipment={shipment}
                  nextStop={nextStop}
                  identifier={identifier}
                  isLoggedIn={isLoggedIn}
                  showBookAppointment={isConsignee || isCarrier}
                  history={this.props.history}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
export default withTranslation()(PublicShipmentTracking);
